import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import UPButton from 'components/button';
import { Box } from '@mui/material';
import { UPDataGrid } from 'components/data-grid';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'store';
import { profileSelector } from 'store/selectors/profile-selectors';
import { CompanyRole } from 'constants/CompanyRole';
import { getAllTranslationsSelector } from 'store/selectors/translations-selector';
import {
  addOrUpdateTranslationAction,
  deleteTranslationAction,
  getAllTranslationsAction,
} from 'store/actions/translation-actions';
import UPInput from 'components/input';
import { escapeRegExp } from 'utils/helpers';
import {
  TRANSLATIONS__LANGUAGE_CODE,
  TRANSLATIONS__DUTCH,
  TRANSLATIONS__FRENCH,
  TRANSLATIONS__ENGLISH,
  TRANSLATIONS__ADD_NEW,
  GENERAL__SEARCH,
} from 'translations/constants';
import { UPButtonStyledWithNoPadding } from 'components/button/styles';
import { ReactComponent as CancelIcon } from '../../assets/icons/CloseGreen.svg';
import { ITranslationRow } from './types';
import { ButtonContainer, GridContainer } from './styles';
import DeleteTranslationModal from './DeleteTranslationModal';

const Translations = (): JSX.Element => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [rowOnHover, setRowOnHover] = useState<number>();
  const [rows, setRows] = useState<ITranslationRow[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [editId, setEditId] = useState<number>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const translationList = useAppSelector(getAllTranslationsSelector);
  const user = useAppSelector(profileSelector);

  useEffect(() => {
    dispatch(getAllTranslationsAction());
  }, [dispatch]);

  const canAccessPage = useCallback(() => {
    const allowedExternalMails = ['vincent@update-pro.be', 'mario@update-pro.be'];
    const customEmails = ['radu', 'vincent', 'mario'];
    const regexPattern = /expertnetwork\.ro/i;
    const neembuRegex = /neembu\.be/i;
    return (
      user &&
      user.role === CompanyRole.ADMIN &&
      (allowedExternalMails.includes(user.email) ||
        customEmails.includes(user.email) ||
        regexPattern.test(user.email) ||
        neembuRegex.test(user.email))
    );
  }, [user]);

  useEffect(() => {
    if (!canAccessPage()) {
      navigate('/dashboard');
    }
  }, [canAccessPage, navigate]);

  const hasEmptyValue = (row: ITranslationRow) => {
    return [row.dutch, row.french, row.english].some(value => value === '' || value == null);
  };

  const emptySort = useCallback((row1: ITranslationRow, row2: ITranslationRow) => {
    const isRow1Empty = hasEmptyValue(row1);
    const isRow2Empty = hasEmptyValue(row2);

    if (isRow1Empty && !isRow2Empty) return -1;
    if (!isRow1Empty && isRow2Empty) return 1;
    return 0;
  }, []);

  useEffect(() => {
    if (translationList) {
      const sorted = translationList.slice().sort(emptySort);
      setRows(sorted);
    }
  }, [translationList, emptySort]);

  const addOrUpdateEntity = useCallback(
    (entity: ITranslationRow, fieldName, newValue: string): ITranslationRow => {
      const updatedEntity: ITranslationRow = {
        ...entity,
        [fieldName]: newValue,
      };

      if (updatedEntity.translationCode) {
        // yes, we allow empty strings to be saved!
        dispatch(addOrUpdateTranslationAction(updatedEntity));
      }

      return updatedEntity;
    },
    [dispatch],
  );

  const deleteTranslation = () => {
    if (editId > 0) {
      dispatch(deleteTranslationAction(editId));
      setEditId(null);
      setDeleteModalOpen(false);
      dispatch(getAllTranslationsAction());
    }
  };

  const onAddNewRow = useCallback(() => {
    setRows(prevState => [
      {
        id: 0 - rows.length,
        translationCode: '',
        dutch: '',
        french: '',
        english: '',
      },
      ...prevState,
    ]);
  }, [rows]);

  const onDeleteRow = useCallback(idToDelete => {
    setRows(prevState => prevState.filter(row => row.id !== idToDelete));
  }, []);

  const onCellEditCommit = params => {
    const { id, field, value } = params;
    if (field === 'translationCode' && rows.some(row => row.id !== id && row.translationCode === value)) {
      onDeleteRow(id);
      onAddNewRow();
      alert('Error: Code must be unique!');

      return;
    }

    // Update the row value
    setRows(prevRows => prevRows.map(row => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const getRowClassName = (params: GridRowParams) => {
    return params.row.id < 0 ? 'MuiDataGrid-custom-background-row' : '';
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: '', hide: true },
    {
      field: 'translationCode',
      headerName: t(TRANSLATIONS__LANGUAGE_CODE),
      width: 450,
      headerAlign: 'left',
      align: 'left',
      editable: true,
      sortable: true,
      sortingOrder: ['asc'],
      valueSetter: params => {
        return addOrUpdateEntity(params.row as ITranslationRow, `translationCode`, params.value?.toString());
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = !params.props.value;
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'dutch',
      headerName: t(TRANSLATIONS__DUTCH),
      width: 350,
      headerAlign: 'left',
      align: 'left',
      editable: true,
      sortable: true,
      sortingOrder: ['asc'],
      valueSetter: params => {
        return addOrUpdateEntity(params.row as ITranslationRow, `dutch`, params.value?.toString());
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = !params.props.value;
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'french',
      headerName: t(TRANSLATIONS__FRENCH),
      width: 350,
      headerAlign: 'left',
      align: 'left',
      editable: true,
      sortable: true,
      sortingOrder: ['asc'],
      valueSetter: params => {
        return addOrUpdateEntity(params.row as ITranslationRow, `french`, params.value?.toString());
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = !params.props.value;
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'english',
      headerName: t(TRANSLATIONS__ENGLISH),
      width: 350,
      headerAlign: 'left',
      align: 'left',
      editable: true,
      sortable: true,
      sortingOrder: ['asc'],
      valueSetter: params => {
        return addOrUpdateEntity(params.row as ITranslationRow, `english`, params.value?.toString());
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = !params.props.value;
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      width: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        if (rowOnHover === params.row.id) {
          return (
            <UPButtonStyledWithNoPadding
              onClick={() => {
                if (+params.row.id > 0) {
                  setEditId(params.row.id);
                  setDeleteModalOpen(true);
                  setSearchText('');
                } else {
                  onDeleteRow(params.row.id);
                }
              }}
            >
              <CancelIcon />
            </UPButtonStyledWithNoPadding>
          );
        }

        return null;
      },
    },
  ];

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue);
    if (searchValue) {
      const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
      const filteredRows = translationList.filter((row: any) => {
        return Object.keys(row).some((field: any) => {
          return searchRegex.test(row[field]);
        });
      });
      setRows(filteredRows);
    } else {
      setRows(translationList);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', margin: '1rem' }}>
        <ButtonContainer>
          <UPButton text={`+ ${t(TRANSLATIONS__ADD_NEW)}`} onClick={onAddNewRow} />
        </ButtonContainer>
        <Box
          sx={{
            width: '300px',
            alignContent: 'center',
            paddingLeft: '1rem',
          }}
        >
          <UPInput
            value={searchText}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              requestSearch(event.target.value);
            }}
            label=""
            placeholder={`${t(GENERAL__SEARCH)}...`}
            icon="search"
          />
        </Box>
      </Box>
      <GridContainer>
        <UPDataGrid
          rows={rows}
          columns={columns}
          setRowOnHover={setRowOnHover}
          withCheckboxes={false}
          getRowClassName={getRowClassName}
          onCellEditCommit={onCellEditCommit}
        />
      </GridContainer>
      {deleteModalOpen && (
        <DeleteTranslationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={deleteTranslation}
        />
      )}
    </Box>
  );
};

export default Translations;
